import React, { useEffect, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import DefaultLayout from "components/DefaultLayout";
import { LectureCompletedType, LectureProps } from '../types';
import Button from 'ui/Button';
import FileUploadList from 'ui/FileUploadList';
import {DatePicker, Input, InputNumber, Select, Switch} from 'components/Form';
import { useLecture } from "hooks/Lectures/useLecture";
import {Checkbox, Form, Input as AntdInput, UploadFile, Row, Col, Radio} from 'antd';
import { Title } from 'ui';
import { RcFile } from 'antd/lib/upload';
import FileDownload from "js-file-download";
import moment from "moment/moment";
import coursesApiClient from "../../../utils/coursesApiClient";
import apiClient from "../../../utils/apiClient";
import {config} from "../../../config/config";
import { TextEditor } from 'ui';
import useHandleError from "../../../utils/useHandleError";
import './styles.scss';

export interface LectureSettingsProps {
    lecture: LectureProps,
    reloadLecture(): any,
}

const LectureSettings: React.FC<LectureSettingsProps> = ({ lecture, reloadLecture }) => {
    const { saveLecture, getLectureFiles, storeLectureFile, deleteLectureFile, lectureLoading, downloadLectureFile } = useLecture();
    const [isFormChanged, setIsFormChanged] = useState(false);
    const [fileList, setFileList] = useState<UploadFile[]>();
    const [setEditor] = useState<any>();
    const [description, setDescription] = useState<string>('');
    const [customFields, setCustomFields] = useState<any>([]);
    const [handleError] = useHandleError();
    const [languageList, setLanguageList] = useState<string[]>([]);
    const [selectedLanguage, setSelectedLanguage] = useState<string>(config.defaultEnvironmentLocale);
    const [titleTranslations, setTitleTranslations] = useState<Record<string, string>>({});
    const [descriptionTranslations, setDescriptionTranslations] = useState<Record<string, string>>({});

    const intl = useIntl();
    const [form] = Form.useForm();

    const formItemLayout = {
        labelCol: { span: 8 },
        wrapperCol: { span: 10 },
    };

    useEffect(() => {
        form.resetFields()

        const initialTitles: Record<string, string> = lecture.titleTranslations ?? {};
        const initialDescriptions: Record<string, string> = lecture.descriptionTranslations ?? {};

        setTitleTranslations({ ...initialTitles });
        setDescriptionTranslations({ ...initialDescriptions });

        const initialTitle = initialTitles[selectedLanguage] ?? lecture.title ?? '';
        const initialDescription = initialDescriptions[selectedLanguage] ?? lecture.description ?? '';

        form.setFieldsValue({
            title: initialTitle,
            completedType: lecture.completedType,
        });
        setDescription(initialDescription);
        fetchFiles();

        if (lecture.customFields && Object.keys(lecture.customFields).length > 0) {
            if (lecture.customFieldValues) {
                Object.entries(lecture.customFieldValues).map((field: any) => {
                    const fieldOptions = lecture.customFields.filter((el: any) => el.uuid === field[0])
                    if (fieldOptions[0].field_type === 'MULTIPLE_CHOICE') {
                        form.setFieldsValue({
                            customFields: {
                                [field[0]]: field[1]
                            }
                        })
                    } else if (fieldOptions[0].field_type === 'DATE') {
                        form.setFieldsValue({
                            customFields: {
                                [field[0]]: moment(field[1])
                            }
                        })
                    } else {
                        form.setFieldsValue({
                            customFields: {
                                [field[0]]: field[1]
                            }
                        })
                    }
                });
            }
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [lecture])

    const loadCustomFields = async () => {
        try {
            const customFields = await coursesApiClient.request('/api/v1/courses/lectures/custom-fields/lecture-custom-fields', [], 'GET');
            setCustomFields(customFields.fields);
        } catch (error) {
            handleError(error)
        } finally {
        }
    }

    const loadLanguages = async () => {
        try {
            const response = await apiClient.request('/api/v1/language', {}, 'GET');
            const codes: string[] = Object.values(response?.languages ?? {}).map((l: any) => l.code);
            setLanguageList(codes);
        } catch (error) {
            handleError(error);
        }
    }

    useEffect(() => {
        loadCustomFields();
        loadLanguages();
    }, []);

    const handleLanguageChange = (language: string) => {
        const currentTitle = form.getFieldValue('title') ?? '';

        const nextTitles = { ...titleTranslations, [selectedLanguage]: currentTitle };
        const nextDescriptions = { ...descriptionTranslations, [selectedLanguage]: description };

        setTitleTranslations(nextTitles);
        setDescriptionTranslations(nextDescriptions);

        setSelectedLanguage(language);

        form.setFieldsValue({ title: nextTitles[language] ?? '' });
        setDescription(nextDescriptions[language] ?? '');
    };

    useEffect(() => {
        form.setFieldsValue({
            files: fileList,
        });

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [fileList])

    useEffect(() => {
        onValuesChange({ description: description });

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [description])

    const fetchFiles = async () => {
        if (lecture && lecture.id) {
            setFileList(await getLectureFiles(lecture.id));
        }
    }

    const onValuesChange = (changedValues: any) => {
        setIsFormChanged(true);
    }

    const handleFormSubmit = async (values: any) => {
        if (!isFormChanged) {
            return;
        }

        interface Fields {
            customFields: {
                [key: string]: string;
            };
        }

        const parsedValues: Fields = {
            customFields: {},
        };

        if (values.customFields !== undefined) {
            parsedValues['customFields'] = values.customFields;
            Object.entries(parsedValues.customFields).map((field: any) => {
                if ((field[1] === null) || (field[1] == undefined) || (field[1] == '')) {
                    delete parsedValues.customFields[field[0]];
                } else {
                    const fieldOptions = customFields.filter((el: any) => el.uuid === field[0])
                    if (fieldOptions[0].field_type === 'DATE') {
                        parsedValues.customFields[field[0]] = field[1].format(config.DBDateFormat);
                    }
                }
            });
        }

        const mergedTitles = { ...titleTranslations, [selectedLanguage]: values.title ?? '' };
        const mergedDescriptions = { ...descriptionTranslations, [selectedLanguage]: description ?? '' };

        const defaultLocale = config.defaultEnvironmentLocale;
        const fallbackTitle = mergedTitles[defaultLocale] ?? mergedTitles[selectedLanguage] ?? values.title;
        const fallbackDescription = mergedDescriptions[defaultLocale] ?? mergedDescriptions[selectedLanguage] ?? description;

        if (await saveLecture({
            id: lecture.id,
            title: fallbackTitle,
            description: fallbackDescription ?? undefined,
            titleTranslations: mergedTitles,
            descriptionTranslations: mergedDescriptions,
            completedType: values.completedType,
            customFields: values.customFields,
        } as LectureProps)) {
            setTitleTranslations(mergedTitles);
            setDescriptionTranslations(mergedDescriptions);
            setIsFormChanged(false);
            reloadLecture();
            // fetchFiles();
        }
    };

    const completedTypeOptions = {
        [LectureCompletedType.Automatic]: intl.formatMessage({ id: 'general.lectures.automatically' }),
        [LectureCompletedType.Manual]: intl.formatMessage({ id: 'general.lectures.manually' }),
    }

    const handleAddFile = async (file: RcFile, callBack: (id?: number) => void) => {
        if (lecture && lecture.id) {
            callBack(await storeLectureFile(lecture.id, file));
        }
    }

    const handleRemoveFile = async (id: number): Promise<boolean> => {
        if (lecture && lecture.id) {
            return await deleteLectureFile(lecture.id, id);
        }

        return false;
    }

    const handleDownload = async (file: any) => {
        if (lecture && lecture.id) {
            const download = file.originFileObj ? file.originFileObj : await downloadLectureFile(lecture.id, +file.uid)

            if (download) {
                FileDownload(download, file.name);
            }

        }
    }

    return (<>
        <Form form={form} onFinish={handleFormSubmit} {...formItemLayout} onValuesChange={onValuesChange}>
            <Title withLine title="general.lectures.description_and_files" />

            <Input
                name='title'
                label={intl.formatMessage({ id: 'general.lectures.title' })}
                customLayout
                customRules={[{ required: true, message: intl.formatMessage({ id: 'validation.field_required' }) }]}
            />

            {languageList.length > 0 &&
                <Form.Item {...formItemLayout} label=' ' className='noColon'>
                    <Radio.Group value={selectedLanguage}>
                        {languageList.map((code: string) => (
                            <Radio.Button
                                key={code}
                                value={code}
                                onClick={() => handleLanguageChange(code)}
                            >
                                {code}
                            </Radio.Button>
                        ))}
                    </Radio.Group>
                </Form.Item>
            }

            <Row>
                <Col span={formItemLayout.labelCol.span}>
                <label className='flex justify-end mr-2'>
                    {intl.formatMessage({ id: 'general.lectures.lecture_description' })} :
                </label>
                </Col>
                <Col span={formItemLayout.wrapperCol.span}>
                    {typeof description !== 'undefined' &&
                        <TextEditor
                            initial={description}
                            setHtmlOutput={setDescription}
                        />
                    }
                </Col>
            </Row>

            <Form.Item
                label={intl.formatMessage({ id: 'general.upload_files' })}
                name="files"
                className='mt-6'
            >
                <FileUploadList
                    multiple={true}
                    onAdd={handleAddFile}
                    onDelete={handleRemoveFile}
                    onDownload={handleDownload}
                    showUploadList={{
                        showDownloadIcon: true,
                    }}
                />
            </Form.Item>

            <Title withLine title="general.lectures.activity_completion_setting" />

            <Select
                showArrow
                name='completedType'
                label={intl.formatMessage({ id: 'general.lectures.mark_activity_completed' })}
                manualOptions={completedTypeOptions}
                customRules={[{ required: true, message: intl.formatMessage({ id: "validation.field_required" }) }]}
                customLayout
            />

            {
                customFields.length > 0 ?
                    <>
                        <Title withLine title="general.custom_fields" />
                        {
                            customFields.map((
                                field: {
                                    uuid: string,
                                    field_type: 'TEXT' | 'INTEGER' | 'DATE' | 'SWITCH' | 'MULTIPLE_CHOICE' | 'HYPERLINK',
                                    title: string,
                                    multiple_choice_options: string[]
                                }) => {

                                switch (field.field_type) {
                                    case 'TEXT':
                                        return (
                                            <Input name={['customFields', field.uuid]} label={field.title}></Input>
                                        )
                                    case 'INTEGER':
                                        return (
                                            <InputNumber name={['customFields', field.uuid]}
                                                         label={field.title}></InputNumber>
                                        )
                                    case 'DATE':
                                        return (
                                            <DatePicker name={['customFields', field.uuid]} label={field.title}
                                                        format={config.defaultDateFormat}></DatePicker>
                                        )
                                    case 'SWITCH':
                                        return (
                                            <Switch name={['customFields', field.uuid]} isFormItem
                                                    label={field.title}></Switch>
                                        )
                                    case 'MULTIPLE_CHOICE':
                                        return (
                                            <div className={'multiple-choice-fields'}>
                                                <Form.Item label={field.title} name={['customFields', field.uuid]}>
                                                    <Checkbox.Group>
                                                        {
                                                            field.multiple_choice_options.map((el:string)=>{
                                                                return (
                                                                    <Checkbox value={el}>
                                                                        {el}
                                                                    </Checkbox>
                                                                )
                                                            })
                                                        }
                                                    </Checkbox.Group>
                                                </Form.Item>
                                            </div>
                                        )
                                    case 'HYPERLINK':
                                        return (
                                            <Form.Item className={'vertical-custom-filer'} label={field.title}
                                                       name={['customFields', field.uuid]}>
                                                <AntdInput.Group compact>
                                                    <Form.Item
                                                        className={'vertical-form-item'}
                                                        label={intl.formatMessage({id: 'users.custom_filter_display_name'})}
                                                        name={['customFields', field.uuid, 'displayName']}
                                                    >
                                                        <AntdInput/>
                                                    </Form.Item>
                                                    <Form.Item
                                                        className={'vertical-form-item'}
                                                        label={intl.formatMessage({id: 'users.custom_filter_hyperlink'})}
                                                        name={['customFields', field.uuid, 'hyperlink']}
                                                    >
                                                        <AntdInput maxLength={1000}/>
                                                    </Form.Item>
                                                </AntdInput.Group>
                                            </Form.Item>
                                        )
                                    default:
                                        return;
                                }
                            })
                        }
                    </>
                    :
                    null
            }

        </Form>
        {!!lecture.isEditable && <DefaultLayout.PageFooter right={
            <>
                <Button disabled={!isFormChanged} loading={lectureLoading}
                    onClick={reloadLecture}
                    message={'general.cancel'}
                    />
                <Button type='primary' disabled={!isFormChanged} loading={lectureLoading}
                    onClick={() => { form.submit() }}
                    message={'general.submit'}
                />
            </>
        } />}
    </>);
};

export default LectureSettings;